import {ServerResponse} from 'http';
import {ParsedUrlQuery} from 'querystring';
import snakeCase from 'lodash/snakeCase';
import router from 'next/router';
import {assert} from './assert';
import {pushLog} from './debug';
import {getKeys} from './object';
import {getIsRouteCancel} from './PageRouter/routeCancelObserver';
import {addOneTimeRouterObserver, removeRouterObserver} from './routerObserver';
import {getLocalStorageObject} from './storage';
import {getFirstMatch} from './string';
import {getIsWebView, getUserAgent} from './userAgent';
import {AUTH_RESULT_PARAMS_LS_KEY} from '../../auth/common';
import {IHistoryTab} from '../../commonSub/type';
import {IContentTabV4} from '../../content/type';
import {IGiftType} from '../../gift/type';
import {HomeTabPage} from '../../home/common/type';
import {MyPageSecondTab} from '../../home/myPage/type';
import {
  IPersonalInformationShortType,
  IThirdPartyTermsType,
} from '../../more/type';
import {ISearchType} from '../../search/type';
import {
  IS_CLIENT,
  WEB_HOST,
  AppUri,
  APP_SCHEME,
  BILLING_WEB_HOST,
} from '../constant';
import {Page} from '../constant/page';
import {callIfFunc, showAuthModal} from '../function';
import {getClientStore} from '../store';

const REGEX_TRAILING_SLASH = /\/$/;
export function needReplace({
  url,
  prevPathname,
}: {
  url: string;
  prevPathname: string;
}) {
  return (
    getPage(url.replace(REGEX_TRAILING_SLASH, '')) ===
    getPage(prevPathname.replace(REGEX_TRAILING_SLASH, ''))
  );
}

let currentHistoryPosition = 0;
export interface IRouteToOption {
  isSendToApp?: boolean;
  isReplace?: boolean;
  isShallow?: boolean;
  isNotCancelable?: boolean;
  isSendToAppAndClose?: boolean;
  query?: ParsedUrlQuery;
}

export function routeTo(...params: Parameters<typeof getRouteAction>) {
  pushLog({msg: 'call routeTo', params});
  if (checkAndShowLogin(params[0]) && !params[1]?.isNotCancelable) {
    return;
  }
  const routeAction = getRouteAction(...params);

  switch (routeAction.type) {
    case 'cancel':
      pushLog({msg: 'routeTo canceled'});
      break;
    case 'assign':
      pushLog({msg: 'routeTo webview location.assign'});
      location.assign(`${WEB_HOST}${routeAction.url}`);
      break;
    case 'assign_and_close': {
      pushLog({msg: 'routeTo webview location.assign and close webview'});
      const uri = `${WEB_HOST}${routeAction.url}`;
      // TODO: uri를 파라미터에 넣어서 호출했을 때, 웹뷰가 먼저 닫히지 않도록 수정 요청하자.
      if (getUserAgent(window.navigator.userAgent)?.isIos) {
        // iOS에서는 location.assign을 두 번 호출이 동작하지 않아 AppUri.Close의 param으로 uri를 넣어준다. (GS-1153)

        routeToApp(AppUri.Close, {uri: encodeURIComponent(uri)});
      } else {
        location.assign(`${WEB_HOST}${uri}`);
        routeToApp(AppUri.Close);
      }
      break;
    }
    case 'replace':
      pushLog({msg: 'routeTo replace'});
      router.replace(routeAction.url, routeAction.asUrl, routeAction.options);
      break;
    case 'push':
      pushLog({msg: 'routeTo push'});
      router.push(routeAction.url, routeAction.asUrl, routeAction.options);
      break;
    case 'external':
      pushLog({msg: 'routeTo external link'});
      window.open(routeAction.url);
      break;
  }
}

const REGEX_DYNAMIC_PATH = /\[/;
function getIsDynamicRoute(route: string): boolean {
  return REGEX_DYNAMIC_PATH.test(route);
}

function encodePathname(url: string) {
  const splits = url.split('?');
  splits[0] = encodeURI(splits[0]);
  return splits.join('?');
}

function getPathnameWithQueryObject(url: string, query: ParsedUrlQuery) {
  const urlArr = url.split('?');
  const searchParams = new URLSearchParams(urlArr[1]);
  const _query = {...query};
  for (const key of searchParams.keys()) {
    const paramValue = searchParams.get(key);
    if (paramValue) _query[key] = paramValue;
  }
  return {pathname: urlArr[0], query: _query};
}

type IRouteUrl = string | {pathname: string; query: ParsedUrlQuery};
type RouteAction =
  | {type: 'cancel'}
  | {type: 'assign' | 'assign_and_close'; url: string}
  | {
      type: 'replace' | 'push';
      url: IRouteUrl;
      asUrl: IRouteUrl;
      options?: object;
    }
  | {type: 'external'; url: string};
export function getRouteAction(
  info: PageInfo | string,
  option: IRouteToOption = {}
): RouteAction {
  const {
    isSendToApp,
    isReplace,
    isShallow = true,
    isNotCancelable,
    isSendToAppAndClose,
    query,
  } = option;

  assert(
    !(isSendToApp === false && isSendToAppAndClose === true),
    'not allowed isSendToAppAndClose is true when isSendToApp is false'
  );

  const page = getPage(info);
  if (!isNotCancelable && getIsRouteCancel({info, option, page})) {
    return {type: 'cancel'};
  }
  let url = getInternalUrl(info);
  url = getIsDynamicRoute(url) ? url : encodePathname(url);
  const asUrl = encodePathname(typeof info === 'string' ? info : getUrl(info));

  const isExternal =
    typeof info === 'string' &&
    info.startsWith('http') &&
    !info.startsWith(WEB_HOST);

  // TODO: 앱하고 동작 맞춰야 한다.(이후 테스트도 수정)
  if (
    getIsWebView() &&
    (isSendToApp ?? isSendToAppAndClose ?? IS_SEND_TO_APP[page])
  ) {
    return {
      type: isSendToAppAndClose ? 'assign_and_close' : 'assign',
      url: asUrl,
    };
  } else if (isExternal) {
    return {type: 'external', url: asUrl};
  } else {
    const _isReplace =
      isReplace !== undefined
        ? isReplace
        : needReplace({
            url: asUrl,
            prevPathname: window.location.pathname,
          });
    const _url = query ? getPathnameWithQueryObject(url, query) : url;
    const _asUrl = query ? getPathnameWithQueryObject(asUrl, query) : asUrl;
    if (_isReplace) {
      return {
        type: 'replace',
        url: _url,
        asUrl: _asUrl,
        options: {
          shallow: isShallow,
          pos: currentHistoryPosition,
        },
      };
    } else {
      currentHistoryPosition++;
      return {
        type: 'push',
        url: _url,
        asUrl: _asUrl,
        options: {pos: currentHistoryPosition},
      };
    }
  }
}

let isRoutingToBack = false;
export function routeBack(params?: Parameters<typeof getRouteBackAction>[0]) {
  pushLog({msg: 'call routeBack', params});
  const routeBackAction = getRouteBackAction(params, getCurrentClientPage());

  if (routeBackAction.type === 'back' || routeBackAction.type === 'replace') {
    if (isRoutingToBack) {
      pushLog({msg: 'routeBack is in progress'});
      return;
    }

    isRoutingToBack = true;
    const onRouteChangeComplete = () => {
      isRoutingToBack = false;
      removeRouterObserver('routeChangeError', onRouteChangeError);
    };
    const onRouteChangeError = () => {
      isRoutingToBack = false;
      removeRouterObserver('routeChangeComplete', onRouteChangeComplete);
    };
    addOneTimeRouterObserver('routeChangeComplete', onRouteChangeComplete);
    addOneTimeRouterObserver('routeChangeError', onRouteChangeError);
  }

  switch (routeBackAction.type) {
    case 'cancel':
      pushLog({msg: 'routeBack canceled'});
      break;
    case 'back':
      pushLog({msg: 'routeBack currentHistoryPosition > 0'});
      router.back();
      break;
    case 'routeToApp':
      pushLog({msg: 'routeBack currentHistoryPosition === 0 webview'});
      routeToApp(AppUri.Close, routeBackAction.param);
      break;
    case 'replace':
      pushLog({msg: 'routeBack currentHistoryPosition === 0 web'});
      router.replace(routeBackAction.url, routeBackAction.asUrl, {
        pos: routeBackAction.pos,
      } as any); // comment-1 참고
      break;
  }
}

type RouteBackAction =
  | {type: 'cancel' | 'back'}
  | {type: 'routeToApp'; param: object}
  | {type: 'replace'; url: string; asUrl: string; pos: number};
export function getRouteBackAction(
  {
    ignoreIsRouteCancel,
    webViewReturnPageInfo,
  }: {
    ignoreIsRouteCancel?: boolean;
    webViewReturnPageInfo?: PageInfo;
  } = {},
  currentClientPage?: Page
): RouteBackAction {
  if (
    !ignoreIsRouteCancel &&
    getIsRouteCancel({currentHistoryPosition, relativePos: 1})
  ) {
    return {type: 'cancel'};
  }

  if (currentHistoryPosition > 0) {
    currentHistoryPosition--;
    return {type: 'back'};
  } else {
    const {return_url} = getQueryObjectFromLocation();
    if (return_url && return_url.startsWith(BILLING_WEB_HOST)) {
      return {
        type: 'replace',
        url: return_url,
        asUrl: return_url,
        pos: currentHistoryPosition,
      };
    }

    if (getIsWebView()) {
      return {
        type: 'routeToApp',
        param: webViewReturnPageInfo
          ? {
              uri: encodeURIComponent(
                `${APP_SCHEME}://${getUrl(webViewReturnPageInfo).substr(1)}`
              ),
            }
          : {},
      };
    } else {
      const backPageInfo = (currentClientPage &&
        callIfFunc(DEFAULT_BACK_PAGE_INFO[currentClientPage])) || {
        page: Page.Home,
      };
      const url = getInternalUrl(backPageInfo);
      const asUrl = getUrl(backPageInfo);
      return {type: 'replace', url, asUrl, pos: currentHistoryPosition};
    }
  }
}

let isMustCancel = false;
export function processBeforePopState(state: {
  url: string;
  as: string;
  options: {pos?: number; shallow?: boolean};
}) {
  const {pos} = state.options;
  const relativePos = currentHistoryPosition - (pos || 0);
  pushLog({
    msg: 'call processBeforePopState',
    params: {isMustCancel, pos, relativePos},
  });
  if (isMustCancel || getIsRouteCancel({currentHistoryPosition, relativePos})) {
    pushLog({msg: 'processBeforePopState cancel'});
    /**
     * 여기서 false를 반환하면 next의 라우팅은 취소되지만 url은 변경된다
     * 변경된 url을 복원하기 위해 go를 호출한다
     * history.go 호출 시에도 processBeforePopState 가 호출된다
     * 이때는 isMustCancel를 이용해서 go가 또 호출되지 않도록 한다
     */
    if (!isMustCancel) {
      history.go(relativePos);
    }
    isMustCancel = !isMustCancel;
    return false;
  } else {
    currentHistoryPosition = pos === undefined ? 0 : pos;
    /**
     * shallow 정보가 state에 저장되서 popState에서도 그대로 사용된다
     * 과거에 shallow true로 라우팅 했다고 popState 시에도 true로 동작하면 문제가 될 수 있다
     * 따라서 popState 시에는 무조건 shallow false로 동작하도록 한다
     * (현재는 state 매개변수가 mutable이지만 혹시라도 immutable로 변경되면 다른 방법을 찾아보자)
     */
    state.options.shallow && (state.options.shallow = false);
    return true;
  }
}

type UriParam = PageInfo | [AppUri, IQuery];
function _getUriInfo(uriParam: UriParam) {
  if ('page' in uriParam) {
    const {page, ...rest} = uriParam;
    return {
      pathname: page,
      params: rest,
    };
  } else {
    return {
      pathname: uriParam[0],
      params: uriParam[1],
    };
  }
}

export function parseParams(uriParam: UriParam) {
  const {pathname, params} = _getUriInfo(uriParam);
  const pathParams: IQuery = {};
  const queryParams: IQuery = {...params};

  pathname
    .replace(REGEX_ALL_ROUTE, '[$1]')
    .split('/')
    .forEach(path => {
      if (path.startsWith('[') && path.endsWith(']')) {
        const param = path.substring(1, path.length - 1);
        if (queryParams[param]) {
          pathParams[param] = queryParams[param];
        }
        delete queryParams[param];
      }
    });

  return {pathParams, queryParams};
}

export function getInternalUrl(pageInfo: PageInfo | string) {
  if (typeof pageInfo === 'string') {
    return getInternalUrlFromString(pageInfo);
  } else {
    const {queryParams} = parseParams(pageInfo);
    const queryStr = getQueryStr(queryParams, true);
    return `/${pageInfo.page}${queryStr ? '?' : ''}${queryStr}`;
  }
}

export function getInternalUrlFromString(input: string) {
  const inputSplits = input.split('?');
  const query = inputSplits[1] || '';
  const path = inputSplits[0].substr(1);
  // [[...home]]에 depth가 하나인 path들이 다 포함된다.
  // dynamic이 아니면서 동일한 path가 있는지 먼저 검사한다.
  if (getKeys(Page).some(key => Page[key] === path)) {
    return input;
  }
  const inputParts = path.split('/');
  for (const parts of DYNAMIC_URL_PARTS_LIST) {
    if (inputParts.length === parts.length) {
      let isPass = true;
      for (let i = 0; i < parts.length; i++) {
        if (parts[i][0] !== '[' && parts[i] !== inputParts[i]) {
          isPass = false;
          break;
        }
      }
      if (isPass) {
        return `/${parts.join('/')}${query ? '?' : ''}${query}`;
      }
    }
  }
  return input;
}

export function getUrl(uriParam: UriParam) {
  const {pathname} = _getUriInfo(uriParam);
  const {pathParams, queryParams} = parseParams(uriParam);
  const queryStr = getQueryStr(queryParams, true);
  return `/${getPathname(pathname, pathParams)}${
    queryStr ? '?' : ''
  }${queryStr}`;
}

const REGEX_ALL_ROUTE = /\[\[...(.+)\]\]/;
function getPathname(uri: string, params: IQuery) {
  const paramKeys = Object.keys(params);
  if (REGEX_ALL_ROUTE.test(uri) && paramKeys.length === 0) return '';

  uri = uri.replace(REGEX_ALL_ROUTE, '[$1]');
  return paramKeys.reduce(
    (pathname, key) => pathname.replace(`[${key}]`, params[key]),
    uri
  );
}

interface IQuery {
  [key: string]: any;
}
export function getQueryStr(query: IQuery, needSnakeCase = false) {
  return Object.keys(query)
    .filter(key => query[key] || query[key] === 0)
    .map(key => `${needSnakeCase ? snakeCase(key) : key}=${query[key]}`)
    .join('&');
}

const DYNAMIC_URL_LIST: string[] = Object.values(Page).filter(item =>
  item.includes('[')
);
const DYNAMIC_URL_PARTS_LIST = DYNAMIC_URL_LIST.map(item => item.split('/'));

export type PageInfo =
  | IPageHome
  | IPageContent
  | IPageContentNotice
  | IPageContentTicketHistory
  | IPageContentTicketRefund
  | IPageContentKeyword
  | IPageContentPurchase
  | IPageSearch
  | IPageEvent
  | IPageMoreCouponRegister
  | IPageGift
  | IPageMoreTicketHistory
  | IPageMyPageItemList
  | IPageMyPageEpisodeList
  | IPageViewer
  | IPageSpecial
  | IPageLuckydraw
  | IPageLuckydrawEnterInfo
  | IPageLuckydrawRaffle
  | IPageCashReceiptDetail
  | IPageInfoNoQuery
  | IPageAddedCashReceipt
  | IPageMoreCashHistory
  | IPageMoreDaumCashHistory
  | IPageMoreInquiry
  | IPageMorePolicyLimit14
  | IPageMorePersonalInformationShort
  | IPageMorePolicyPaymentManagement
  | IPageMorePolicyCookieTerms
  | IPageMorePolicyPersonalInformation
  | IPageMorePolicyPersonalInformationKor20210801
  | IPageMorePolicyPersonalInformationKor20211115
  | IPageMorePolicyPersonalInformationKor20220530
  | IPageMoreBusiness
  | IPageMorePolicyTerms
  | IPageMorePolicyTerms20210801
  | IPageMorePolicyTerms20221021
  | IPageMorePolicyTermsRevision
  | IPageMorePolicyPaidTerms
  | IPageMorePolicyPaidTerms20220630
  | IPageMorePolicyPaidTerms20221021
  | IPageMorePolicyPaidTermsRevision
  | IPageMorePolicyAdvertisement
  | IPageLinkApp
  | IPageMorePolicyLegalRepresentative
  | IPageMorePolicyChildPersonalInformation
  | IPageMorePolicyChildPersonalThirdParty
  | IPageMoreNotice;

interface IPageLuckydraw {
  page: Page.Luckydraw;
  drawId: number;
  rewardId: number;
  luckydrawId: string;
}

interface IPageGift {
  page: Page.Gift;
  tab?: IGiftType;
}

interface IPageMorePolicyAdvertisement {
  page: Page.MorePolicyAdvertisement;
  space?: number;
}

interface IPageLuckydrawEnterInfo {
  page: Page.LuckydrawEnterinfo;
  luckydrawId?: string;
  rewardId: number;
  presentId?: number;
  drawId?: number;
}

interface IPageLuckydrawRaffle {
  page: Page.LuckydrawRaffle;
  uid: string;
  rewardId: number;
}
interface IPageHome {
  page: Page.Home;
  home?: HomeTabPage;
  tab?: string;
}

interface IPageContent {
  page: Page.Content;
  id: number;
  sid: string;
  tab?: IContentTabV4;
  collectionId?: string;
  baseId?: number;
}
interface IPageContentNotice {
  page: Page.ContentNotice;
  id: number;
  sid: string;
  focusId?: number;
}
interface IPageContentTicketHistory {
  page: Page.ContentTicketHistory;
  id: number;
  sid: string;
  tab?: IHistoryTab;
}
interface IPageContentTicketRefund {
  page: Page.ContentTicketRefund;
  id: number;
  sid: string;
  ticketId: number;
}

interface IPageContentKeyword {
  page: Page.ContentKeyword;
  keyword: string;
}

interface IPageContentPurchase {
  page: Page.ContentPurchase;
  id: number;
  sid: string;
  episodeId?: number;
  episodeSid?: string;
  isTryReadAgain?: boolean;
}

interface IPageSearch {
  page: Page.Search;
  keyword?: string;
  type?: ISearchType;
}
interface IPageEvent {
  page: Page.Event;
  eventId: number;
}

interface IPageMoreCouponRegister {
  page: Page.MoreCouponRegister;
  code?: string;
}

interface IPageMoreCashHistory {
  page: Page.MoreCashHistory;
  tab?: IHistoryTab;
}

interface IPageMoreTicketHistory {
  page: Page.MoreTicketHistory;
  tab?: IHistoryTab;
}

interface IPageMoreDaumCashHistory {
  page: Page.MoreDaumCashHistory;
  tab?: IHistoryTab;
}

interface IPageMyPageItemList {
  page: Page.MyPageItemList;
  tab: MyPageSecondTab;
  isEditMode?: boolean;
}
interface IPageMyPageEpisodeList {
  page: Page.MyPageEpisodeList;
  contentId: number;
  contentSid: string;
}
interface IPageViewer {
  page: Page.Viewer;
  id: number;
  sid: string;
}

interface IPageSpecial {
  page: Page.Special;
  id: number;
}

interface IPageCashReceiptDetail {
  page: Page.MoreCashReceiptDetail;
  id: number;
}

interface IPageMoreInquiry {
  page: Page.MoreInquiry;
  space?: number;
}

interface IPageMorePolicyLimit14 {
  page: Page.MorePolicyLimit14;
  space?: number;
}

interface IPageMorePersonalInformationShort {
  page: Page.MorePersonalInformationShort;
  space?: number;
  type: IPersonalInformationShortType;
}

interface IPageMorePolicyPaymentManagement {
  page: Page.MorePolicyPaymentManagement;
  space?: number;
}

interface IPageMorePolicyCookieTerms {
  page: Page.MorePolicyCookieTerms;
  space?: number;
}
interface IPageMorePolicyPersonalInformation {
  page: Page.MorePolicyPersonalInformation;
  space?: number;
}

interface IPageMorePolicyPersonalInformationKor20210801 {
  page: Page.MorePolicyPersonalInformationKor20210801;
  space?: number;
}

interface IPageMorePolicyPersonalInformationKor20211115 {
  page: Page.MorePolicyPersonalInformationKor20211115;
  space?: number;
}

interface IPageMorePolicyPersonalInformationKor20220530 {
  page: Page.MorePolicyPersonalInformationKor20220530;
  space?: number;
}

interface IPageAddedCashReceipt {
  page: Page.AddedCashReceipt;
  id: number;
}

interface IPageMoreBusiness {
  page: Page.MoreBusiness;
  space?: number;
}

interface IPageLinkApp {
  page: Page.LinkApp;
  store: boolean;
}

interface IPageMorePolicyTerms {
  page: Page.MorePolicyTerms;
  space?: number;
}

interface IPageMorePolicyTerms20210801 {
  page: Page.MorePolicyTermsKor20210801;
  space?: number;
}
interface IPageMorePolicyTerms20221021 {
  page: Page.MorePolicyTermsKor20221021;
  space?: number;
}
interface IPageMorePolicyTermsRevision {
  page: Page.MorePolicyTermsKorRevision;
  space?: number;
}
interface IPageMorePolicyPaidTerms {
  page: Page.MorePolicyPaidTerms;
  space?: number;
}
interface IPageMorePolicyPaidTerms20220630 {
  page: Page.MorePolicyPaidTermsKor20220630;
  space?: number;
}
interface IPageMorePolicyPaidTerms20221021 {
  page: Page.MorePolicyPaidTermsKor20221021;
  space?: number;
}
interface IPageMorePolicyPaidTermsRevision {
  page: Page.MorePolicyPaidTermsKorRevision;
  space?: number;
}

interface IPageMorePolicyLegalRepresentative {
  page: Page.MorePolicyLegalRepresentative;
  space?: number;
}

interface IPageMorePolicyChildPersonalInformation {
  page: Page.MorePolicyChildPersonalInformation;
  space?: number;
}

interface IPageMorePolicyChildPersonalThirdParty {
  page: Page.MorePolicyChildPersonalThirdParty;
  space?: number;
  type: IThirdPartyTermsType;
}
interface IPageMoreNotice {
  page: Page.MoreNotice;
  focusId?: number;
}

interface IPageInfoNoQuery {
  page:
    | Page.NotSupported
    | Page.MoreAutoCashPurchase
    | Page.MoreCashPurchase
    | Page.MoreCashReceiptInfo
    | Page.MoreCashReceiptRegister
    | Page.MoreCashPassInfo
    | Page.More
    | Page.MoreFaq
    | Page.MoreMyNews
    | Page.MoreSetting
    | Page.MoreSettingThirdParty
    | Page.MoreCustomerCenter
    | Page.MoreRightReport
    | Page.MoreMyInformation
    | Page.MoreMyInformationChangeNickname
    | Page.MoreMyInformationServiceWithdrawal
    | Page.MoreServiceInformation
    | Page.MorePolicy
    | Page.MorePolicyManagement
    | Page.MorePolicyYouth
    | Page.WebviewNotice
    | Page.AuthTerms
    | Page.AuthOAuth
    | Page.AuthIntegration
    | Page.MorePolicyPersonalThirdParty
    | Page.MoreWaitfreeNotification
    | Page.MoreAutoCashPurchaseComplete
    | Page.DevSsrError
    | Page.MoreCashPassInfoDetail;
}

const REGEX_A_HREF = /<a\s+(?:[^>]*?\s+)?href=(["'])(.*?)\1/g;
export function getAnchorHrefValues(html: string) {
  const matches = [];
  while (html) {
    const match = REGEX_A_HREF.exec(html);
    if (match === null) {
      break;
    }
    matches.push(match[2]);
  }
  return matches;
}

export function redirectTo({
  pageInfo = {page: Page.Home},
  res,
  extraQuery,
}: {
  pageInfo?: string | PageInfo;
  res?: ServerResponse;
  extraQuery?: ParsedUrlQuery;
}) {
  const _url = typeof pageInfo === 'string' ? pageInfo : getUrl(pageInfo);
  const url = extraQuery ? getPathnameWithQueryObject(_url, extraQuery) : _url;
  if (res) {
    res.writeHead(302, {
      Location: encodeURI(
        typeof url === 'string'
          ? url
          : `${url.pathname}${url.query ? `?${getQueryStr(url.query)}` : ''}`
      ),
    });
    res.end();
  } else {
    /**
     * comment-1
     * 타입에서는 shallow 만 허용하지만, 실제로는 pushState 의 데이터로 그대로 입력된다
     * 그리고 입력된 값이 processBeforePopState 의 options 로 넘어온다
     * Next.js 버전이 올라가면서 기능이 달라질 위험이 있으므로 버전 올릴 때는 꼭 확인해보자
     */
    router.replace(url, url, {pos: currentHistoryPosition} as any);
  }
}

const REGEX_APP_SCHEME = /^kakaowebtoon:\/\/(.*)/;
export function parseLandingUriFromServer(uri?: string) {
  if (!uri) {
    return '';
  }
  const path = uri && getFirstMatch(REGEX_APP_SCHEME, uri);
  return typeof path === 'string' ? `/${path}` : uri;
}

export function getCurrentPathOnClient() {
  return window.location.origin + window.location.pathname;
}

export function getQueryObjectFromLocation() {
  if (location.search) {
    const query = location.search.substr(1);
    return getQueryObject(query);
  } else {
    return {};
  }
}
export function getQueryObject(query: string) {
  const result: {[key: string]: string} = {};
  query.split('&').forEach(item => {
    const parts = item.split('=');
    const key = parts[0];
    const value = parts[1];
    if (key && value) {
      result[key] = decodeURIComponent(value);
    }
  });
  return result;
}

export function getRouterQueryObject() {
  return router.query;
}

export function routeToApp(appUri: AppUri, queryParams: IQuery = {}) {
  pushLog({msg: 'call routeToApp', params: {appUri, queryParams}});
  location.assign(`${WEB_HOST}${getUrl([appUri, queryParams])}`);
}

const DEFAULT_BACK_PAGE_INFO: {
  [key in Page]?: PageInfo | (() => PageInfo);
} = {
  [Page.MoreCouponRegister]: {page: Page.More},
  [Page.MoreSetting]: {page: Page.More},
  [Page.MoreMyInformation]: {page: Page.More},
  [Page.MoreMyInformationChangeNickname]: {page: Page.MoreMyInformation},
  [Page.MoreMyInformationServiceWithdrawal]: {page: Page.MoreMyInformation},
  [Page.MoreServiceInformation]: {
    page: Page.MoreCustomerCenter,
  },
  [Page.MorePolicy]: {
    page: Page.More,
  },
  [Page.MoreBusiness]: {
    page: Page.More,
  },
  [Page.MorePolicyAdvertisement]: getPolicyBackPageInfoFromLocation,
  [Page.MorePolicyManagement]: getPolicyBackPageInfoFromLocation,
  [Page.MorePolicyPaidTerms]: getPolicyBackPageInfoFromLocation,
  [Page.MorePolicyTerms]: getPolicyBackPageInfoFromLocation,
  [Page.MorePolicyYouth]: getPolicyBackPageInfoFromLocation,
  [Page.MorePolicyPersonalInformation]: getPolicyBackPageInfoFromLocation,
  [Page.ContentNotice]: getContentPageInfoFromLocation,
  [Page.ContentTicketHistory]: getContentPageInfoFromLocation,
  [Page.ContentTicketRefund]: getContentTicketHistoryPageInfoFromLocation,
  [Page.ContentPurchase]: getContentPageInfoFromLocation,
  [Page.MorePolicyPersonalThirdParty]: getThirdPartyBackPageInfoFromLocation,
  [Page.MoreWaitfreeNotification]: {page: Page.More},
  [Page.MoreFaq]: {
    page: Page.MoreCustomerCenter,
  },
  [Page.MoreRightReport]: {
    page: Page.MoreCustomerCenter,
  },
  [Page.MyPageEpisodeList]: {
    page: Page.Home,
    home: HomeTabPage.MyPage,
  },
  [Page.MoreInquiry]: {
    page: Page.MoreCustomerCenter,
  },
  [Page.Viewer]: getContentPageInfoAtViewer,
  [Page.MoreSettingThirdParty]: {page: Page.More},
  [Page.MoreNotice]: {page: Page.More},
};

function getContentPageInfoFromLocation(): PageInfo {
  const {id, sid} = router.query;
  return {page: Page.Content, id: Number(id), sid: String(sid)};
}
function getContentTicketHistoryPageInfoFromLocation(): PageInfo {
  const {id, sid} = router.query;
  return {page: Page.ContentTicketHistory, id: Number(id), sid: String(sid)};
}

function getContentPageInfoAtViewer(): PageInfo {
  const content = getClientStore()!.getState().viewer.content;
  return content
    ? {
        page: Page.Content,
        id: content.id,
        sid: content.sid,
      }
    : {page: Page.Home};
}

function getIsAuth() {
  return !!getLocalStorageObject({key: AUTH_RESULT_PARAMS_LS_KEY});
}

function getPolicyBackPageInfoFromLocation(): PageInfo {
  return getIsAuth() ? {page: Page.AuthTerms} : {page: Page.MorePolicy};
}

function getThirdPartyBackPageInfoFromLocation(): PageInfo {
  return getIsAuth()
    ? {page: Page.AuthTerms}
    : {page: Page.MoreSettingThirdParty};
}

export function getCurrentClientPage(): Page {
  return router.route.substr(1) as Page;
}

export function getPage(pageInfo: PageInfo | string): Page {
  if (typeof pageInfo === 'string') {
    return getInternalUrlFromString(pageInfo).split('?')[0].substr(1) as Page;
  } else {
    return pageInfo.page;
  }
}

export function prefetch(page: Page) {
  router.prefetch('/' + page);
}

export function getQueryValue(param: string) {
  return IS_CLIENT ? getQueryObjectFromLocation()[param] : undefined;
}

export function getUrlWithFilteredQuery(
  url: string,
  predicate: (query: string) => boolean
) {
  const [pathname, query] = url.split('?');
  if (query) {
    const filteredQuery = query
      .split('&')
      .map(q => q.split('='))
      .filter(([k]) => predicate(k))
      .map(([k, v]) => `${k}=${v}`)
      .join('&');
    return `${pathname}${filteredQuery ? '?' + filteredQuery : ''}`;
  } else {
    return url;
  }
}

// 앱에 존재하거나 앱에서 처리되는 페이지면 true로 설정한다.
const IS_SEND_TO_APP: {[key in Page]: boolean} = {
  [Page.Home]: true,
  [Page.Search]: true,
  [Page.Event]: false,
  [Page.DebugLog]: false,
  [Page.Gift]: true,
  [Page.Luckydraw]: true,
  [Page.LuckydrawEnterinfo]: true,
  [Page.LuckydrawRaffle]: true,
  [Page.More]: true,
  [Page.MoreCouponRegister]: true,
  [Page.MoreSetting]: true,
  [Page.MoreSettingThirdParty]: true,
  [Page.MoreFaq]: true,
  [Page.MoreMyNews]: true,
  [Page.MoreNotice]: true,
  [Page.MoreWaitfreeNotification]: true,
  [Page.MoreTicketHistory]: true,
  [Page.MoreCashHistory]: true,
  [Page.MoreDaumCashHistory]: true,
  [Page.MoreServiceInformation]: false,
  [Page.MoreCustomerCenter]: false,
  [Page.MoreRightReport]: false,
  [Page.MoreInquiry]: false,
  [Page.MorePolicy]: false,
  [Page.MorePolicyTerms]: false,
  [Page.MorePolicyTermsKor20210801]: false,
  [Page.MorePolicyTermsKor20221021]: false,
  [Page.MorePolicyTermsKorRevision]: false,
  [Page.MorePolicyPaidTerms]: false,
  [Page.MorePolicyPaidTermsKor20220630]: false,
  [Page.MorePolicyPaidTermsKor20221021]: false,
  [Page.MorePolicyPaidTermsKorRevision]: false,
  [Page.MorePolicyPaymentManagement]: false,
  [Page.MorePolicyPersonalInformation]: false,
  [Page.MorePolicyPersonalInformationKor20210801]: false,
  [Page.MorePolicyPersonalInformationKor20211115]: false,
  [Page.MorePolicyPersonalInformationKor20220530]: false,
  [Page.MorePolicyLegalRepresentative]: false,
  [Page.MorePolicyChildPersonalInformation]: false,
  [Page.MorePolicyChildPersonalThirdParty]: false,
  [Page.MorePolicyManagement]: false,
  [Page.MorePolicyAdvertisement]: false,
  [Page.MorePolicyPersonalThirdParty]: false,
  [Page.MorePolicyLimit14]: false,
  [Page.MorePersonalInformationShort]: false,
  [Page.MorePolicyYouth]: false,
  [Page.MoreBusiness]: false,
  [Page.MoreMyInformation]: true,
  [Page.MorePolicyCookieTerms]: false,
  [Page.MoreMyInformationChangeNickname]: true,
  [Page.MoreMyInformationServiceWithdrawal]: true,
  [Page.Content]: true,
  [Page.ContentNotice]: true,
  [Page.ContentPurchase]: true,
  [Page.ContentTicketHistory]: true,
  [Page.ContentTicketRefund]: true,
  [Page.ContentKeyword]: true,
  [Page.MyPageItemList]: true,
  [Page.MyPageEpisodeList]: true,
  [Page.Viewer]: true,
  [Page.Special]: true,
  [Page.WebviewNotice]: false,
  [Page.AddedCashReceipt]: false,
  [Page.NotSupported]: false,
  [Page.AuthTerms]: false,
  [Page.AuthOAuth]: false,
  [Page.AuthIntegration]: false,
  [Page.Dev]: false,
  [Page.DevSsrError]: false,
  [Page.MoreCashPurchase]: false,
  [Page.MoreAutoCashPurchase]: false,
  [Page.MoreAutoCashPurchaseComplete]: false,
  [Page.MoreCashReceiptInfo]: false,
  [Page.MoreCashReceiptDetail]: false,
  [Page.MoreCashReceiptRegister]: false,
  [Page.MoreCashPassInfo]: false,
  [Page.MoreCashPassInfoDetail]: false,
  [Page.LinkApp]: false,
};

function checkAndShowLogin(url: PageInfo | string) {
  if (!IS_CLIENT) return false;
  const page = getPage(url);
  const apiParamsAuthResult =
    getClientStore()?.getState().auth.apiParamsAuthResult;
  const user = getClientStore()?.getState().auth.user;
  const isLogin = !!user;
  const isNeedLogin = apiParamsAuthResult
    ? false
    : IS_LOGIN_REQUIRED_PAGES.includes(page) && !isLogin;
  isNeedLogin && showAuthModal();
  return isNeedLogin;
}

export const IS_LOGIN_REQUIRED_PAGES: Page[] = [
  Page.MoreAutoCashPurchase,
  Page.MoreCashPassInfo,
  Page.MoreCashPurchase,
  Page.MoreCashReceiptDetail,
  Page.MoreCashReceiptInfo,
  Page.MoreCashReceiptRegister,
  Page.ContentPurchase,
  Page.ContentTicketHistory,
  Page.ContentTicketRefund,
  Page.MyPageEpisodeList,
  Page.Luckydraw,
  Page.LuckydrawEnterinfo,
  Page.LuckydrawRaffle,
  Page.MoreTicketHistory,
  Page.MoreCashHistory,
  Page.MoreCouponRegister,
  Page.MoreMyInformation,
  Page.MoreMyInformationChangeNickname,
  Page.MoreMyInformationServiceWithdrawal,
];

/**
 * 현재 웹툰에서 사용되는 모든 쿼리 파라미터
 * 외부 사이트 혹은 특정 앱 내에서 접근할때 추가되는 쿼리 삭제를 위함
 * 참고: https://kakaopagecorp.slack.com/archives/G01KNBN5HCN/p1675386859334349
 *
 * TODO: 새로운 쿼리가 추가되면 아래에도 넣어야하는게 불편하다. 타입도 없어 검증도 어렵다. 개선 방법을 찾아보자.
 */
export const QUERY_PARAMS_OF_ALL_PAGES = [
  'draw_id',
  'reward_id',
  'luckydraw_id',
  'tab',
  'space',
  'present_id',
  'uid',
  'collection_id',
  'base_id',
  'ticket_id',
  'keyword',
  'episode_id',
  'episode_sid',
  'is_try_read_again',
  'type',
  'event_id',
  'is_signup',
  'is_edit_mode',
  'content_id',
  'content_sid',
  'id',
  'sid',
  'focus_id',
  'rid',
  'home',
  'code',
  'name',
  'store',
  'session_id',
  'signup_status',
  'signup_type',
  'auth_status',
  'error_type',
  'error_message',
  'verification_session_id',
  'friend_redeem_code',
];
